/*
  Gaia Iluka — Creator Circle: Creator Spotlight carousel controls
  File: assets/gi-cc-spotlight.js

  Progressive enhancement, and nothing more. The track in
  sections/gi-cc-spotlight.liquid is a native overflow-x list with CSS
  scroll-snap: swipe, trackpad, click-drag and (because the track carries
  tabindex="0") keyboard arrow keys all work with this file absent,
  blocked or broken. Everything here is additive — the arrows and the
  dots ship hidden and are only revealed once this script has measured
  that there is actually something to scroll.

  Same shape as assets/gi-home-table-carousel.js, deliberately: one
  scrollBy() per click and let the browser do the snapping. No library,
  no rAF scroll loop, no transform track, no scroll-linked animation.

  WHY THE DOTS ARE BUILT HERE RATHER THAN IN LIQUID. How many pages there
  are depends on the viewport — four cards per view on a desktop, three
  on a tablet, 1.2 on a phone — so Liquid cannot know the count at render
  time. Rendering them here also means a visitor without JavaScript never
  sees a row of dots that cannot do anything.

  WRAP, NOT CLONE. Past the last page the next arrow returns to the
  first, and vice versa. The alternative — duplicating slides to fake an
  endless track — would enter every creator into the accessibility tree
  twice and report a list length that is a lie, so it is not done here.

  REDUCED MOTION. The CSS drops scroll-behavior to auto under
  prefers-reduced-motion; this file matches it by passing behavior
  'auto' to scrollTo, since a CSS scroll-behavior does not override an
  explicit behavior passed to a scroll method.
*/
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-cc-sp-track]';
  var instances = typeof WeakMap === 'function' ? new WeakMap() : null;

  function prefersReducedMotion() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function createController(track) {
    var carousel = track.closest('.cc-sp__carousel');
    if (!carousel) return null;

    var nav = carousel.querySelector('[data-cc-sp-nav]');
    var prevBtn = carousel.querySelector('[data-cc-sp-prev]');
    var nextBtn = carousel.querySelector('[data-cc-sp-next]');
    var dotsEl = carousel.querySelector('[data-cc-sp-dots]');

    var pageCount = 0;
    var currentPage = 0;
    var ticking = false;

    function maxScroll() {
      return Math.max(0, track.scrollWidth - track.clientWidth);
    }

    /* A "page" is one viewport of the track. Ceil + 1 so a partial last
       page still gets a dot of its own — otherwise the final cards are
       reachable by swipe but not by the controls. */
    function computePages() {
      var width = track.clientWidth;
      if (width <= 0) return 1;
      return Math.max(1, Math.ceil(maxScroll() / width) + 1);
    }

    function pageFromScroll() {
      var width = track.clientWidth;
      if (width <= 0) return 0;
      var page = Math.round(track.scrollLeft / width);
      return Math.min(Math.max(page, 0), pageCount - 1);
    }

    function goToPage(page) {
      var width = track.clientWidth;
      var target = Math.min(page * width, maxScroll());
      track.scrollTo({
        left: target,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    }

    function renderDots() {
      if (!dotsEl) return;

      // Nothing to page through: no dots at all, rather than one dot.
      if (pageCount < 2) {
        dotsEl.innerHTML = '';
        dotsEl.hidden = true;
        return;
      }

      dotsEl.innerHTML = '';
      for (var i = 0; i < pageCount; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'cc-sp__dot';
        dot.setAttribute('data-cc-sp-page', String(i));
        dot.setAttribute('aria-label', 'Go to creators, page ' + (i + 1) + ' of ' + pageCount);
        dot.setAttribute('aria-current', i === currentPage ? 'true' : 'false');
        dotsEl.appendChild(dot);
      }
      dotsEl.hidden = false;
    }

    function syncDots() {
      if (!dotsEl || dotsEl.hidden) return;
      var dots = dotsEl.querySelectorAll('.cc-sp__dot');
      for (var i = 0; i < dots.length; i++) {
        dots[i].setAttribute('aria-current', i === currentPage ? 'true' : 'false');
      }
    }

    function measure() {
      var scrollable = maxScroll() > 1;

      // Two permanently useless buttons are worse than none.
      if (nav) nav.hidden = !scrollable;

      var nextPageCount = scrollable ? computePages() : 1;
      var changed = nextPageCount !== pageCount;
      pageCount = nextPageCount;
      currentPage = pageFromScroll();

      if (changed) {
        renderDots();
      } else {
        syncDots();
      }
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        var page = pageFromScroll();
        if (page !== currentPage) {
          currentPage = page;
          syncDots();
        }
      });
    }

    function onPrev() {
      // Wrap to the end rather than dead-ending at the first card.
      goToPage(currentPage <= 0 ? pageCount - 1 : currentPage - 1);
    }

    function onNext() {
      goToPage(currentPage >= pageCount - 1 ? 0 : currentPage + 1);
    }

    function onDotClick(event) {
      var dot = event.target.closest ? event.target.closest('[data-cc-sp-page]') : null;
      if (!dot) return;
      var page = parseInt(dot.getAttribute('data-cc-sp-page'), 10);
      if (!isNaN(page)) goToPage(page);
    }

    if (prevBtn) prevBtn.addEventListener('click', onPrev);
    if (nextBtn) nextBtn.addEventListener('click', onNext);
    if (dotsEl) dotsEl.addEventListener('click', onDotClick);
    track.addEventListener('scroll', onScroll, { passive: true });

    var resizeObserver = null;
    if (typeof ResizeObserver === 'function') {
      resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(track);
    } else {
      window.addEventListener('resize', measure, { passive: true });
    }

    measure();

    // Images arrive after first paint and change scrollWidth; re-measure
    // once everything has settled so the dot count is not short.
    window.addEventListener('load', measure);

    return {
      destroy: function () {
        if (prevBtn) prevBtn.removeEventListener('click', onPrev);
        if (nextBtn) nextBtn.removeEventListener('click', onNext);
        if (dotsEl) dotsEl.removeEventListener('click', onDotClick);
        track.removeEventListener('scroll', onScroll);
        window.removeEventListener('load', measure);
        if (resizeObserver) {
          resizeObserver.disconnect();
        } else {
          window.removeEventListener('resize', measure);
        }
      },
    };
  }

  function init(track) {
    if (!track || !instances || instances.has(track)) return;
    var controller = createController(track);
    if (controller) instances.set(track, controller);
  }

  function destroy(track) {
    if (!track || !instances) return;
    var controller = instances.get(track);
    if (!controller) return;
    controller.destroy();
    instances.delete(track);
  }

  function initWithin(scope) {
    if (!scope || !scope.querySelectorAll) return;
    var tracks =
      scope.matches && scope.matches(ROOT_SELECTOR)
        ? [scope]
        : scope.querySelectorAll(ROOT_SELECTOR);
    Array.prototype.forEach.call(tracks, init);
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  ready(function () {
    initWithin(document);
  });

  document.addEventListener('shopify:section:load', function (evt) {
    initWithin(evt.target);
  });

  document.addEventListener('shopify:section:unload', function (evt) {
    var track =
      evt.target && evt.target.querySelector ? evt.target.querySelector(ROOT_SELECTOR) : null;
    if (track) destroy(track);
  });
})();
